import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { startRegistration } from "@simplewebauthn/browser";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Field, ErrorBox } from "@/components/common";
import { ShieldCheck, Fingerprint, Check, Copy, Download, LogOut, KeyRound } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: "app", label: "Authenticator app" },
  { id: "backup", label: "Backup codes" },
  { id: "passkey", label: "Passkey (optional)" },
];

function Stepper({ current }) {
  return (
    <ol className="flex items-center gap-2 text-xs" data-testid="mfa-setup-stepper">
      {STEPS.map((s, i) => {
        const state = i < current ? "done" : i === current ? "active" : "todo";
        return (
          <li key={s.id} className="flex items-center gap-2">
            <span className={`h-6 w-6 rounded-full grid place-items-center font-medium border ${state === "done" ? "bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white" : state === "active" ? "bg-primary border-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>
              {state === "done" ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={state === "active" ? "font-medium text-foreground" : "text-muted-foreground"}>{s.label}</span>
            {i < STEPS.length - 1 && <span className="w-8 border-t border-border" />}
          </li>
        );
      })}
    </ol>
  );
}

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const { me, refresh, logout } = useAuth();
  const [step, setStep] = useState(0);
  const needsPassword = !!me?.password_change_required;
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwDone, setPwDone] = useState(false);
  const [totp, setTotp] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState([]);
  const [saved, setSaved] = useState(false);
  const [pkName, setPkName] = useState("");
  const [pkDone, setPkDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const start = useCallback(async () => {
    setBusy(true); setError(null);
    try { const { data } = await api.post("/me/mfa/totp/start"); setTotp(data); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }, []);
  useEffect(() => { if (!me?.user?.mfa_enabled && !needsPassword) start(); }, [start, me?.user?.mfa_enabled, needsPassword]);

  const changePassword = async () => {
    if (pw.next !== pw.confirm) { setError("The new passwords do not match"); return; }
    setBusy(true); setError(null);
    try {
      await api.post("/me/password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated"); setPwDone(true); setPw({ current: "", next: "", confirm: "" });
      await refresh();
      if (!me?.user?.mfa_enabled) start();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const confirm = async (v) => {
    const c = v || code; if (c.length !== 6) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/me/mfa/totp/confirm", { code: c });
      setBackup(data.backup_codes || []); setCode(""); setStep(1); toast.success("Authenticator confirmed");
      await refresh();
    } catch (e) { setError(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };

  const copyCodes = async () => { try { await navigator.clipboard.writeText(backup.join("\n")); toast.success("Backup codes copied"); } catch (e) { toast.error("Could not copy. Please download instead."); } };
  const downloadCodes = () => {
    const blob = new Blob([`VoroSuite Sales backup codes for ${me?.user?.email}\n\n${backup.join("\n")}\n\nEach code works once. Keep this file somewhere safe.`], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "vorosuite-backup-codes.txt"; a.click();
  };

  const addPasskey = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/me/passkeys/register/options");
      const credential = await startRegistration({ optionsJSON: data.options });
      await api.post(`/me/passkeys/register/verify/${data.challenge_id}`, { credential, name: pkName || null });
      setPkDone(true); toast.success("Passkey added"); await refresh();
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "Passkey prompt was cancelled or is not supported on this device. You can add one later in Settings." : e?.name === "InvalidStateError" ? "This authenticator is already registered." : errMsg(e));
    } finally { setBusy(false); }
  };

  const finish = async () => { await refresh(); navigate("/today", { replace: true }); };

  return (
    <div className="min-h-screen bg-background grid place-items-center p-6">
      <div className="w-full max-w-2xl space-y-4">
        <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-bold">V</div><span className="font-heading font-semibold">VoroSuite Sales</span></div>
        <div className="panel p-6 space-y-5" data-testid="mfa-setup-card">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-primary"><ShieldCheck className="h-5 w-5" /><span className="text-xs font-medium uppercase tracking-wide">Required security step</span></div>
            <h1 className="text-xl font-heading font-semibold">Protect your account</h1>
            <p className="text-sm text-muted-foreground">{needsPassword && !pwDone ? "Two quick steps: choose your own password, then set up your authenticator app." : "Your organisation requires an authenticator app for every member. This takes about a minute and you only do it once."}</p>
          </div>
          {needsPassword && !pwDone ? (
            <div className="space-y-3" data-testid="password-setup-step">
              <div className="rounded-md border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning-bg))] p-3 text-sm">
                <div className="font-medium flex items-center gap-1"><KeyRound className="h-4 w-4" />Set a new password first</div>
                <p className="meta mt-1">You signed in with a temporary password issued by your administrator. Choose your own before continuing (12+ characters; not a common password).</p>
              </div>
              {error && <ErrorBox error={error} />}
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Temporary password"><Input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" /></Field>
                <Field label="New password"><Input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" /></Field>
                <Field label="Confirm new password"><Input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></Field>
              </div>
              <Button onClick={changePassword} disabled={busy || pw.current.length < 8 || pw.next.length < 12 || pw.confirm.length < 12} data-testid="pw-submit">{busy ? "Saving…" : "Save new password"}</Button>
            </div>
          ) : (<>
          <Stepper current={step} />
          {error && <ErrorBox error={error} />}

          {step === 0 && (
            <div className="grid md:grid-cols-[200px_1fr] gap-5 items-start" data-testid="mfa-setup-step-app">
              <div className="bg-white p-2 rounded-md border border-border inline-block min-h-[180px] min-w-[180px] grid place-items-center">
                {totp ? <QRCodeSVG value={totp.otpauth_uri} size={176} /> : <span className="meta">Preparing…</span>}
              </div>
              <div className="space-y-3 text-sm">
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Open Microsoft Authenticator, Google Authenticator, 1Password or Authy.</li>
                  <li>Scan the QR code, or enter this key manually:</li>
                </ol>
                {totp && <code className="block font-mono text-xs break-all bg-muted p-2 rounded" data-testid="mfa-setup-secret">{totp.secret}</code>}
                <p className="meta">Then type the 6-digit code the app shows.</p>
                <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) confirm(v); }} data-testid="mfa-setup-otp">
                  <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
                </InputOTP>
                <div className="flex gap-2">
                  <Button onClick={() => confirm()} disabled={busy || code.length !== 6} data-testid="mfa-setup-confirm">{busy ? "Checking…" : "Confirm code"}</Button>
                  <Button variant="ghost" onClick={start} disabled={busy} data-testid="mfa-setup-regenerate">New QR code</Button>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="mfa-setup-step-backup">
              <div className="rounded-md border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning-bg))] p-3 text-sm">
                <div className="font-medium">Save these backup codes now - they are shown only once.</div>
                <p className="meta mt-1">Use one if you lose your phone. Each code works a single time.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 font-mono text-xs mt-3" data-testid="mfa-setup-backup-codes">{backup.map((c) => <span key={c} className="bg-card border border-border rounded px-2 py-1">{c}</span>)}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={copyCodes} data-testid="mfa-setup-copy-codes"><Copy className="h-4 w-4 mr-1" />Copy</Button>
                <Button variant="outline" size="sm" onClick={downloadCodes} data-testid="mfa-setup-download-codes"><Download className="h-4 w-4 mr-1" />Download</Button>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-setup-saved-check" /> I have stored these codes somewhere safe</label>
              <Button onClick={() => setStep(2)} disabled={!saved} data-testid="mfa-setup-next">Continue</Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="mfa-setup-step-passkey">
              <div className="flex items-start gap-3">
                <Fingerprint className="h-5 w-5 text-primary mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium">Add a passkey for faster sign-in (optional)</div>
                  <p className="text-muted-foreground">Use Face ID, Touch ID, Windows Hello or a security key instead of a password and code. Passkeys are phishing-resistant and bound to this site.</p>
                </div>
              </div>
              {pkDone ? (
                <div className="chip chip-success">Passkey registered</div>
              ) : (
                <div className="flex gap-2"><Input placeholder="Name this device (e.g. Work laptop)" value={pkName} onChange={(e) => setPkName(e.target.value)} className="max-w-xs" data-testid="mfa-setup-passkey-name" /><Button variant="outline" onClick={addPasskey} disabled={busy} data-testid="mfa-setup-add-passkey"><Fingerprint className="h-4 w-4 mr-1" />Add passkey</Button></div>
              )}
              <div className="flex gap-2 pt-1">
                <Button onClick={finish} data-testid="mfa-setup-finish">{pkDone ? "Go to my day" : "Skip for now and continue"}</Button>
              </div>
            </div>
          )}
          </>)}
        </div>
        <div className="flex items-center justify-between meta">
          <span>Signed in as {me?.user?.email}</span>
          <button className="inline-flex items-center gap-1 underline" onClick={async () => { await logout(); navigate("/login"); }} data-testid="mfa-setup-signout"><LogOut className="h-3 w-3" /> Sign out</button>
        </div>
      </div>
    </div>
  );
}
